// Shared protected-shell summary of Custom Disclaimers enrollment metrics and the disclaimer
// list itself. Mounted once above the routed pages so multiple consumers (e.g. a dashboard
// widget and the Custom Disclaimers overview page) read the same data without each triggering
// its own network requests.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { enrollmentStatusService } from '../services/enrollmentStatusService';
import { customDisclaimerService } from '../services/customDisclaimerService';
import type { CustomDisclaimer } from '../types/customDisclaimers';

export type EnrollmentStatusLoadState = 'loading' | 'ready' | 'error';

export interface DisclaimerEnrollmentSummaryContextValue {
  enrollmentState: EnrollmentStatusLoadState;
  disclaimerState: EnrollmentStatusLoadState;
  organizationEnrolledUserCount: number | null;
  usersReceivingByDisclaimerId: ReadonlyMap<string, number>;
  disclaimers: readonly CustomDisclaimer[];
  disclaimerCount: number | null;
  assignedDisclaimerCount: number | null;
  unassignedDisclaimerCount: number | null;
  refreshEnrollmentStatus(): Promise<void>;
  refreshDisclaimers(): Promise<void>;
}

const DisclaimerEnrollmentSummaryContext = createContext<DisclaimerEnrollmentSummaryContextValue | undefined>(
  undefined,
);

// Stable empty instances so a metrics failure (or list failure) resets state to a consistent,
// referentially-stable "nothing loaded" shape rather than allocating a new empty collection
// on every failed attempt.
const EMPTY_USERS_RECEIVING: ReadonlyMap<string, number> = new Map();
const EMPTY_DISCLAIMERS: readonly CustomDisclaimer[] = [];

export const DisclaimerEnrollmentSummaryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [enrollmentState, setEnrollmentState] = useState<EnrollmentStatusLoadState>('loading');
  const [organizationEnrolledUserCount, setOrganizationEnrolledUserCount] = useState<number | null>(null);
  const [usersReceivingByDisclaimerId, setUsersReceivingByDisclaimerId] =
    useState<ReadonlyMap<string, number>>(EMPTY_USERS_RECEIVING);

  const [disclaimerState, setDisclaimerState] = useState<EnrollmentStatusLoadState>('loading');
  const [disclaimers, setDisclaimers] = useState<readonly CustomDisclaimer[]>(EMPTY_DISCLAIMERS);
  const [disclaimerCount, setDisclaimerCount] = useState<number | null>(null);
  const [assignedDisclaimerCount, setAssignedDisclaimerCount] = useState<number | null>(null);
  const [unassignedDisclaimerCount, setUnassignedDisclaimerCount] = useState<number | null>(null);

  // Each read has its own monotonic token. The initial mount load and a later manual retry can
  // both be in flight at once; only the response matching the latest token for its own read is
  // applied, so a slower/older response (initial or retry) can never clobber a newer one. The
  // two reads use separate refs so an enrollment failure/retry can never affect the disclaimer
  // list's token (and vice versa) - the two load states are fully independent.
  const latestEnrollmentRequestRef = useRef(0);
  const latestDisclaimerRequestRef = useRef(0);

  const refreshEnrollmentStatus = useCallback(async () => {
    const requestToken = ++latestEnrollmentRequestRef.current;
    setEnrollmentState('loading');

    try {
      const status = await enrollmentStatusService.getEnrollmentStatus();
      if (latestEnrollmentRequestRef.current !== requestToken) {
        return;
      }

      setOrganizationEnrolledUserCount(status.organizationEnrolledUserCount);
      setUsersReceivingByDisclaimerId(
        new Map(status.disclaimerCounts.map((count) => [count.disclaimerId, count.usersReceiving])),
      );
      setEnrollmentState('ready');
    } catch {
      if (latestEnrollmentRequestRef.current !== requestToken) {
        return;
      }

      setOrganizationEnrolledUserCount(null);
      setUsersReceivingByDisclaimerId(EMPTY_USERS_RECEIVING);
      setEnrollmentState('error');
    }
  }, []);

  const refreshDisclaimers = useCallback(async () => {
    const requestToken = ++latestDisclaimerRequestRef.current;
    setDisclaimerState('loading');

    try {
      const result = await customDisclaimerService.list();
      if (latestDisclaimerRequestRef.current !== requestToken) {
        return;
      }

      const assignedCount = result.filter((disclaimer) => disclaimer.policyIds.length > 0).length;

      setDisclaimers(result);
      setDisclaimerCount(result.length);
      setAssignedDisclaimerCount(assignedCount);
      setUnassignedDisclaimerCount(result.length - assignedCount);
      setDisclaimerState('ready');
    } catch {
      if (latestDisclaimerRequestRef.current !== requestToken) {
        return;
      }

      setDisclaimers(EMPTY_DISCLAIMERS);
      setDisclaimerCount(null);
      setAssignedDisclaimerCount(null);
      setUnassignedDisclaimerCount(null);
      setDisclaimerState('error');
    }
  }, []);

  // Load both reads exactly once when the protected shell mounts. refreshEnrollmentStatus and
  // refreshDisclaimers are stable (empty dependency arrays), so this deliberately runs only on
  // mount rather than re-running when they are (re)used for manual retries.
  useEffect(() => {
    refreshEnrollmentStatus();
    refreshDisclaimers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsubscribe = customDisclaimerService.subscribe(() => {
      refreshDisclaimers();
    });

    return unsubscribe;
  }, [refreshDisclaimers]);

  const value = useMemo<DisclaimerEnrollmentSummaryContextValue>(
    () => ({
      enrollmentState,
      disclaimerState,
      organizationEnrolledUserCount,
      usersReceivingByDisclaimerId,
      disclaimers,
      disclaimerCount,
      assignedDisclaimerCount,
      unassignedDisclaimerCount,
      refreshEnrollmentStatus,
      refreshDisclaimers,
    }),
    [
      enrollmentState,
      disclaimerState,
      organizationEnrolledUserCount,
      usersReceivingByDisclaimerId,
      disclaimers,
      disclaimerCount,
      assignedDisclaimerCount,
      unassignedDisclaimerCount,
      refreshEnrollmentStatus,
      refreshDisclaimers,
    ],
  );

  return (
    <DisclaimerEnrollmentSummaryContext.Provider value={value}>
      {children}
    </DisclaimerEnrollmentSummaryContext.Provider>
  );
};

export const useDisclaimerEnrollmentSummary = (): DisclaimerEnrollmentSummaryContextValue => {
  const context = useContext(DisclaimerEnrollmentSummaryContext);
  if (!context) {
    throw new Error('useDisclaimerEnrollmentSummary must be used within a DisclaimerEnrollmentSummaryProvider');
  }
  return context;
};
